import './sim.css';
import type { World } from '../engine/World';
import type { Timeline, TimelineEntry, EffectType, WorldEvent } from '../types';
import { EFFECT_META, DEFAULT_DUR } from '../effects/registry';
import { EPOCHS, RHYTHM } from '../config';
import { eventFromDanmaku, eventFromComment, midHashOf } from '../oracle/localOracle';
import { DanmakuOverlay } from './danmaku';
import { rand, randInt } from '../engine/util';
import { sfx } from '../engine/Sfx';

/**
 * 神谕模拟器:全项目基石(GDD Phase0 §0.2)。
 * 既是开发测试工具,又是第一期视频的拍摄工具(时间轴无人值守回放)。
 * 按 ` 呼出面板。
 */
export class Simulator {
  private dm: DanmakuOverlay;
  private caption: HTMLElement;
  private panel!: HTMLElement;
  private timers: number[] = [];
  private raf = 0;
  private playing = false;
  private tlStart = 0;
  private tlDur = 0;
  private timelines = new Map<string, Timeline>();

  /** enabled=false(线上普通用户):不建面板、不挂快捷键、无悬浮按钮——
   *  弹幕层与字幕仍在(服务端事件回放需要),但没有任何本地触发入口。 */
  constructor(private world: World, worldPane: HTMLElement, private simHost: HTMLElement, enabled = true) {
    this.dm = new DanmakuOverlay(worldPane);
    this.caption = document.createElement('div');
    this.caption.id = 'sub-caption';
    worldPane.appendChild(this.caption);
    if (enabled) {
      this.buildPanel();
      this.bindKeys();
    }
  }

  // ── 注入原语(模拟器 & 时间轴共用)──
  danmaku(text: string, count = 1, spreadSec = 0) {
    const n = Math.min(count, 60);
    for (let i = 0; i < n; i++) {
      const at = spreadSec > 0 ? (i / n) * spreadSec * 1000 + rand(0, 180) : rand(0, 120);
      this.after(at, () => this.dm.shoot(text));
    }
    // 每条文案只合成**一发**事件(按合力计 synergy=count)。
    // 旧逻辑 ceil(count/6) 发:92 条对轰弹幕 = 16 个事件 × 2s 错峰 = 32 秒队列,
    // 把 77s 的天空撕裂主角挤到 95s+,且同类事件互顶纯属浪费(EP1 录制实锤)。
    const at = (spreadSec > 0 ? spreadSec * 500 : 0) + 480 + rand(0, 160);
    this.after(at, () => {
      const ev = eventFromDanmaku(text, { kind: 'danmaku', mid_hash: midHashOf(randInt(10000, 999999)), raw: text }, count);
      if (ev) this.world.playEvent(ev);
    });
  }

  comment(text: string, uname = '晚风') {
    this.dm.shoot(`${uname}:${text}`, 'comment');
    this.after(520, () => {
      const ev = eventFromComment(text, { kind: 'comment', uname, raw: text, mid_hash: midHashOf(uname) });
      this.world.playEvent(ev);
    });
  }

  coin(delta: number) {
    sfx.play('coin');
    this.world.state.addFaith(delta);
    if (delta >= RHYTHM.coinBurstThreshold) {
      this.dm.coinRain(Math.ceil(delta / 4));
      const ev: WorldEvent = {
        type: 'meteor_shower', intensity: 0.9, duration_sec: 12,
        flavor_text: '香火鼎盛!神明倾倒了一整条星河。', source: { kind: 'coin', raw: `+${delta}` },
      };
      this.world.playEvent(ev);
    } else if (delta > 0) {
      this.dm.coinRain(Math.min(delta, 12));
    }
  }

  fx(type: EffectType, intensity = 0.75) {
    const meta = EFFECT_META[type];
    this.world.playEvent({
      type, intensity, duration_sec: DEFAULT_DUR[type] ?? 15,
      flavor_text: '', source: { kind: 'system', raw: meta.label },
    });
  }

  private after(ms: number, fn: () => void) {
    const id = window.setTimeout(fn, ms);
    this.timers.push(id);
  }

  // ── 时间轴回放 ──
  register(tl: Timeline) { this.timelines.set(tl.name, tl); this.refreshTimelineOptions(); }

  async loadFromUrl(url: string, name: string) {
    try {
      const r = await fetch(url);
      const tl = (await r.json()) as Timeline;
      tl.name = tl.name || name;
      this.register(tl);
    } catch (e) { console.warn('时间轴加载失败', url, e); }
  }

  private bgmEl: HTMLAudioElement | null = null;

  playTimeline(name: string) {
    const tl = this.timelines.get(name);
    if (!tl) return;
    this.stopTimeline();
    // 时间轴自带 BGM(如 EP1 全长版):回放期间独立播放,音量给旁白让位
    if (tl.bgm) {
      this.bgmEl = new Audio(tl.bgm);
      this.bgmEl.volume = 0.42;
      this.bgmEl.play().catch(() => { this.bgmEl = null; });
    }
    this.playing = true;
    this.tlStart = performance.now();
    this.tlDur = Math.max(...tl.entries.map((e) => e.at_sec)) * 1000 + 3000;
    for (const e of tl.entries) this.after(e.at_sec * 1000, () => this.runEntry(e));
    this.tickProgress();
    this.setCaption(`▶ 回放:${tl.name}`, 1600);
  }

  private runEntry(e: TimelineEntry) {
    switch (e.kind) {
      case 'danmaku': this.danmaku(e.text ?? '', e.count ?? 1, e.spreadSec ?? 0); break;
      case 'comment': this.comment(e.text ?? '', e.uname ?? '神明'); break;
      case 'coin': this.coin(e.delta ?? 0); break;
      case 'ambient': /* mood via世界 dispatch 已足够;此处可扩展 */ break;
      case 'marker':
        // 字幕时长跟随台词长度(≈旁白语速):修"字幕 3.2s 固定,长句旁白念一半字幕没了"
        if (e.note) this.setCaption(e.note, Math.min(12500, 1800 + e.note.length * 260));
        if (e.vo) { const a = new Audio(e.vo); a.volume = 1; a.play().catch(() => { /* 未解锁/缺文件时静默 */ }); }
        break;
    }
  }

  stopTimeline() {
    for (const id of this.timers) clearTimeout(id);
    this.timers = [];
    this.playing = false;
    if (this.bgmEl) { this.bgmEl.pause(); this.bgmEl = null; }
    cancelAnimationFrame(this.raf);
    const bar = this.panel?.querySelector<HTMLElement>('#tl-progress > div');
    if (bar) bar.style.width = '0%';
  }

  private tickProgress = () => {
    if (!this.playing) return;
    const p = Math.min(1, (performance.now() - this.tlStart) / this.tlDur);
    const bar = this.panel?.querySelector<HTMLElement>('#tl-progress > div');
    if (bar) bar.style.width = `${p * 100}%`;
    if (p >= 1) { this.playing = false; return; }
    this.raf = requestAnimationFrame(this.tickProgress);
  };

  private setCaption(text: string, ms: number) {
    this.caption.textContent = text;
    this.caption.classList.add('show');
    window.setTimeout(() => this.caption.classList.remove('show'), ms);
  }

  // ── 面板 ──
  private buildPanel() {
    const p = document.createElement('div');
    p.id = 'sim-panel';
    const fxButtons = (Object.keys(EFFECT_META) as EffectType[])
      .map((t) => `<button data-fx="${t}" class="tier-${EFFECT_META[t].tier}">${EFFECT_META[t].label}</button>`)
      .join('');
    const jump = EPOCHS.map((e, i) => `<button data-epoch="${i}">${e.name}</button>`).join('');
    p.innerHTML = `
      <h3>⚡ 神谕模拟器</h3>
      <div class="sec">
        <div class="label">神迹(点击即触发 · 颜色=等级)</div>
        <div class="fx-grid">${fxButtons}</div>
      </div>
      <div class="sec">
        <div class="label">发一条弹幕(轻神谕)</div>
        <div class="row"><input type="text" id="in-dm" placeholder="如:我要下雨 / 放烟花 / 撕裂天空"><button id="send-dm">发送</button></div>
        <div class="row"><input type="text" id="in-dm-multi" placeholder="轰炸文案"><input type="number" id="in-dm-count" value="30" style="max-width:64px"><button id="send-dm-multi">轰炸</button></div>
      </div>
      <div class="sec">
        <div class="label">写一句圣诗(评论 · ≥40字或成诗→刻碑)</div>
        <div class="row"><input type="text" id="in-cm" placeholder="给灵墟写一句诗…"><button id="send-cm">投稿</button></div>
      </div>
      <div class="sec">
        <div class="label">投币(香火)</div>
        <div class="row">
          <button data-coin="10">+10</button>
          <button data-coin="50">+50 爆发</button>
          <button data-coin="200">+200</button>
          <input type="number" id="in-coin" value="500" style="max-width:70px"><button id="send-coin">投</button>
        </div>
      </div>
      <div class="sec">
        <div class="label">纪元速跳(测试用)</div>
        <div class="fx-grid">${jump}</div>
      </div>
      <div class="sec">
        <div class="label">时间轴回放(录视频)</div>
        <div class="row"><select id="tl-select"></select><button id="tl-play">▶ 播放</button><button id="tl-stop">■ 停</button></div>
        <div id="tl-progress"><div></div></div>
      </div>
      <div class="sec">
        <div class="label">录制</div>
        <div class="toggles">
          <label><input type="checkbox" id="tg-cinematic"> 影院模式(隐藏外壳)</label>
          <label><input type="checkbox" id="tg-worldonly"> 仅世界(全宽)</label>
        </div>
        <div class="sim-hint">\` 键开关本面板 · 影院模式下按 \` 仍可呼出</div>
      </div>
    `;
    this.simHost.appendChild(p);
    this.panel = p;

    const fab = document.createElement('button');
    fab.className = 'sim-fab';
    fab.textContent = '⚡ 模拟器 (`)';
    fab.onclick = () => this.toggle();
    document.body.appendChild(fab);

    // 绑定
    p.querySelectorAll<HTMLButtonElement>('[data-fx]').forEach((b) => {
      b.onclick = () => this.fx(b.dataset.fx as EffectType);
    });
    p.querySelectorAll<HTMLButtonElement>('[data-epoch]').forEach((b) => {
      b.onclick = () => {
        const idx = Number(b.dataset.epoch);
        const target = EPOCHS[idx].faith + 1;
        const d = target - this.world.state.faith;
        if (d > 0) this.world.state.addFaith(d);
      };
    });
    p.querySelectorAll<HTMLButtonElement>('[data-coin]').forEach((b) => {
      b.onclick = () => this.coin(Number(b.dataset.coin));
    });
    const dmIn = p.querySelector<HTMLInputElement>('#in-dm')!;
    p.querySelector<HTMLButtonElement>('#send-dm')!.onclick = () => { if (dmIn.value.trim()) this.danmaku(dmIn.value.trim()); };
    dmIn.addEventListener('keydown', (e) => { if (e.key === 'Enter' && dmIn.value.trim()) this.danmaku(dmIn.value.trim()); });
    p.querySelector<HTMLButtonElement>('#send-dm-multi')!.onclick = () => {
      const t = p.querySelector<HTMLInputElement>('#in-dm-multi')!.value.trim() || '下雨';
      const c = Number(p.querySelector<HTMLInputElement>('#in-dm-count')!.value) || 30;
      this.danmaku(t, c, 4);
    };
    const cmIn = p.querySelector<HTMLInputElement>('#in-cm')!;
    p.querySelector<HTMLButtonElement>('#send-cm')!.onclick = () => { if (cmIn.value.trim()) this.comment(cmIn.value.trim()); };
    p.querySelector<HTMLButtonElement>('#send-coin')!.onclick = () => this.coin(Number(p.querySelector<HTMLInputElement>('#in-coin')!.value) || 0);
    p.querySelector<HTMLButtonElement>('#tl-play')!.onclick = () => this.playTimeline(p.querySelector<HTMLSelectElement>('#tl-select')!.value);
    p.querySelector<HTMLButtonElement>('#tl-stop')!.onclick = () => this.stopTimeline();
    p.querySelector<HTMLInputElement>('#tg-cinematic')!.onchange = (e) => document.body.classList.toggle('cinematic', (e.target as HTMLInputElement).checked);
    p.querySelector<HTMLInputElement>('#tg-worldonly')!.onchange = (e) => document.body.classList.toggle('world-only', (e.target as HTMLInputElement).checked);
  }

  private refreshTimelineOptions() {
    const sel = this.panel?.querySelector<HTMLSelectElement>('#tl-select');
    if (!sel) return;
    sel.innerHTML = '';
    for (const name of this.timelines.keys()) {
      const o = document.createElement('option');
      o.value = name; o.textContent = name;
      sel.appendChild(o);
    }
  }

  private bindKeys() {
    window.addEventListener('keydown', (e) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === '`' || e.key === '·' || e.key === '~') { e.preventDefault(); this.toggle(); }
    });
  }

  toggle() { this.panel?.classList.toggle('open'); }
  open() { this.panel?.classList.add('open'); }
}
